import React, { useEffect, useState } from 'react';
import {
  Button,
  FormCell,
  Grid,
  ISelectOption,
  ISpruceFieldProps,
  Select,
  SpruceField,
  ToggleSwitch,
} from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { AutoSubmit } from '../shared/AutoSubmit';
import { useFormikContext } from 'formik';
import { forbiddenInternalOrderStatuses } from '../../types/clients/CreateDefaultContact';
import { eInternalOrderStatus, prettyInternalOrderStatus } from '../../ui-toolkit/enums/eInternalOrderStatus';

interface IDefaultContactEditOrdersDialogProps {
  onNoClick: () => void;
  getAffectedOrders: (values: any) => void;
  preliminaryAffectedOrderFetchStatus: IFetchStatus;
}

const statuses = Object.values(eInternalOrderStatus);
const statusOptions: ISelectOption[] = statuses
  .filter((s) => !forbiddenInternalOrderStatuses.includes(s))
  .map((s) => ({
    value: s,
    label: prettyInternalOrderStatus[s],
  }));

const getUpperStatusOptions = (lowerStatus: eInternalOrderStatus): ISelectOption[] => {
  const lowerIndex = statuses.indexOf(lowerStatus);
  return statusOptions.slice(lowerIndex + 1);
};

export const DefaultContactEditOrdersDialog: React.FC<IDefaultContactEditOrdersDialogProps> = ({
                                                                                                 onNoClick,
                                                                                                 getAffectedOrders,
                                                                                                 preliminaryAffectedOrderFetchStatus,
                                                                                               }) => {
  const [shouldGetAffectedOrders, setShouldGetAffectedOrders] = useState(false);
  const { values, isValid } = useFormikContext();

  //reset getAffectedOrders
  useEffect(() => {
    setShouldGetAffectedOrders(false);
  }, [preliminaryAffectedOrderFetchStatus.fetched]);

  return (
    <>
      <SpruceField name={'updateOrders'}>
        {({ field, formik }: ISpruceFieldProps) => (
          <>
            <FormCell label={'Would you like to apply these changes to existing orders?'}>
              <Grid columnCount={2}>
                <Button
                  intent={'primary'}
                  type={'button'}
                  onClick={() => {
                    formik.setFieldTouched(field.name);
                    field.onChange(true);
                  }}
                >
                  Yes
                </Button>
                <Button
                  intent={'secondary'}
                  type={'button'}
                  onClick={async () => {
                    formik.setFieldTouched(field.name);
                    await field.onChange(false);
                    onNoClick();
                  }}
                >
                  No
                </Button>
              </Grid>
            </FormCell>
            {field.value && (
              <>
                <SpruceField name={'statusRange'}>
                  {({ field }: ISpruceFieldProps) => (
                    <ToggleSwitch
                      value={'Status Range'}
                      label={'Status Range'}
                      checked={field.value}
                      id={'status-range-toggle'}
                      onChange={(name, value) => {
                        field.onChange(value);
                      }}
                    />
                  )}
                </SpruceField>
                <Grid columnCount={2}>
                  <SpruceField name={'statusLowerRange'} inputType={'select'}>
                    {({ field }: ISpruceFieldProps) => (
                      <Select
                        {...field}
                        value={field.value ? statusOptions.find((s) => s.value === field.value) : null}
                        options={statusOptions}
                        onChange={(value: ISelectOption) => {
                          field.onChange(value);
                          setShouldGetAffectedOrders(true);
                        }}
                        label={(values as any).statusRange ? 'Order Status From' : 'Order Status'}
                      />
                    )}
                  </SpruceField>
                  {(values as any).statusRange && (
                    <SpruceField name={'statusUpperRange'} inputType={'select'}>
                      {({ field, formik }: ISpruceFieldProps) => (
                        <Select
                          {...field}
                          value={field.value ? statusOptions.find((s) => s.value === field.value) : null}
                          options={getUpperStatusOptions(formik.values['statusLowerRange'])}
                          onChange={(value: ISelectOption) => {
                            field.onChange(value);
                            setShouldGetAffectedOrders(true);
                          }}
                          label={'Order Status To'}
                        />
                      )}
                    </SpruceField>
                  )}
                </Grid>
                <AutoSubmit
                  isValid={isValid && shouldGetAffectedOrders && !preliminaryAffectedOrderFetchStatus.fetching}
                  values={values}
                  submitForm={() => {
                    getAffectedOrders(values);
                  }}
                />
              </>
            )}
          </>
        )}
      </SpruceField>
    </>
  );
};
